import { FormattedMessage } from 'react-intl'
import { call, put, select, takeLatest } from 'redux-saga/effects'

import { buildScopeFilterParams } from '@components/CreatorSearch/utils/filterParams'
import {
  bulkRejectAgentSourcedCreators,
  bulkRejectAgentSourcedCreatorsFailure,
  bulkRejectAgentSourcedCreatorsSuccess,
  fetchAgentSourcedCreators,
} from '@store/agentSourcedCreators/actions'
import {
  IBulkRejectAgentSourcedCreatorsPayload,
  isBulkRejectAllPending,
} from '@store/agentSourcedCreators/types'
import { selectAuthToken } from '@store/auth/selectors'
import { fetchCampaign } from '@store/campaigns'
import { extractVerbatimErrorMessage } from '@store/discoveryRuns/api'
import { toast, Toaster } from '@tribegroup'
import { networkRequest } from '@utils/networkRequest'

// The BE renders bulk_reject as a normal creators collection and reports how
// many creators it actually rejected inside the `metadata` block, alongside
// the usual pagination keys — NOT at the body root
// (`render_sourced_creators_collection(extra_metadata: { rejected_count: ... })`
// merges into `index_metadata`). Returned on BOTH request forms.
//
// This was read at the root in the first cut, which meant the count was never
// found and AC 4 silently degraded to the count-less toast on every path. Do
// not "defensively" re-add a root read: the root is the body root (see
// fetchCreators, which reads `response.metadata`), so a root
// `rejected_count` is not a shape the BE can produce, and tolerating it would
// let a future contract break hide behind a fallback instead of surfacing.
interface IBulkRejectResponse {
  metadata?: {
    rejected_count?: number
  }
}

const readRejectedCount = (response: IBulkRejectResponse | undefined): number | undefined => {
  const reported = response?.metadata?.rejected_count
  return typeof reported === 'number' ? reported : undefined
}

// BRAND-9514/9550: PUT the COLLECTION-level bulk_reject link in ONE of two
// mutually exclusive forms (both or neither -> 422 invalid_bulk_reject_ids):
//
//   { ids: [12, 34], comment? }                        page-select (9514)
//   { all_pending: true, sourcing_run?, <filters>, comment? }   select-all (9550)
//
// The select-all form ships a SCOPE, not a set: the list is BE-paginated, so
// an id list can only ever name the loaded page (Archer repro: 23 of 52, then
// 25 of 64, both with a success toast). The scope mirrors exactly what the
// user is viewing — run scope plus the membership-scoping filters, built from
// the same helper the list query uses so the two cannot drift.
//
// The BE stays all-or-nothing: a 422 changes NOTHING, so the failure path
// refreshes nothing and the view keeps its selection.
const buildRequestBody = (
  payload: IBulkRejectAgentSourcedCreatorsPayload,
): Record<string, unknown> => {
  // `comment` is omitted from the body entirely when blank.
  const comment = payload.comment ? { comment: payload.comment } : {}

  if (!isBulkRejectAllPending(payload)) {
    return { ids: payload.ids, ...comment }
  }

  // `sourcing_run` is ALWAYS sent — an ordinal, or 'all'. There is no
  // "omit it and let the BE work out which run I meant" branch, and there
  // must never be one again: omitting it made the BE re-derive the default
  // run AT WRITE TIME (`default_run_ids` = newest ready run), so a rerun that
  // finished importing while the tab sat open silently redirected the reject
  // onto a run the AM was never looking at (BRAND-9550). The view binds the
  // concrete run at confirm time; `BulkRejectRunScope` keeps `null` out of
  // this function by construction. Mirrors the list query's
  // `?sourcing_run=<ordinal>|all` (fetchCreators' appendRunScopeParam) for
  // every case EXCEPT the omission, which is a read-only affordance.
  return {
    all_pending: true,
    sourcing_run: String(payload.sourcingRun),
    ...buildScopeFilterParams(payload.filters),
    ...comment,
  }
}

export function* bulkRejectCreatorsSaga(action: ReturnType<typeof bulkRejectAgentSourcedCreators>) {
  const { campaignId, filters, href, method } = action.payload
  try {
    const token: string = yield select(selectAuthToken)
    const response: IBulkRejectResponse | undefined = yield call(
      networkRequest,
      method,
      href,
      buildRequestBody(action.payload),
      token,
    )
    yield put(bulkRejectAgentSourcedCreatorsSuccess())
    yield put(fetchCampaign(campaignId))
    // Refetch WITH the view's filters (carried on the action — adversarial
    // finding 1): dropping them here reloaded the unfiltered list under the
    // user's still-active filter chrome. Run scope is selected fresh from
    // redux inside fetchCreatorsSaga, so it needs no forwarding.
    yield put(fetchAgentSourcedCreators({ campaignId, filters }))
    // AC 4 (BRAND-9550): report what the BACKEND actually rejected. The
    // select-all form has no FE-side count at all — the whole point is that
    // the FE never loaded the full set — so when the response carries no
    // count there is no honest number and the toast drops it rather than
    // inventing one. The ids form keeps its pre-9550 fallback so a BE that
    // reports nothing still reads exactly as it did before.
    const rejectedCount =
      readRejectedCount(response) ??
      (isBulkRejectAllPending(action.payload) ? undefined : action.payload.ids.length)
    if (rejectedCount === undefined) {
      yield call(Toaster.createToast, FormattedMessage, {
        id: 'creatorSearch.vibeCheck.bulkReject.toast.successNoCount',
      })
    } else {
      yield call(Toaster.createToast, FormattedMessage, {
        id: 'creatorSearch.vibeCheck.bulkReject.toast.success',
        values: { count: rejectedCount },
      })
    }
  } catch (e) {
    // House 422-verbatim toast pattern: surface the BE's own message text.
    const message = extractVerbatimErrorMessage(e)
    yield put(bulkRejectAgentSourcedCreatorsFailure(message))
    yield call(toast, message)
  }
}

export default function* watchBulkRejectAgentSourcedCreators() {
  yield takeLatest(bulkRejectAgentSourcedCreators.type, bulkRejectCreatorsSaga)
}
